<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useInventoryDashboardStore } from '../pages/useInventoryDashboardStore';
import { InventoryDashboardViewModel } from '../pages/inventoryDashboardViewModel';
import { i18n } from '@/i18n';
import { computed } from 'vue';
import { MetricTypes } from '../domains/entities/Dashboard';
import KpiChart from './chart/KpiChart.vue';
import { Kpi } from '../domains/entities/Kpi';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import AmountChart from './chart/AmountChart.vue';
const t = i18n.t;

const store = useInventoryDashboardStore();
const { state } = storeToRefs(store);
const {} = store;
const vm = new InventoryDashboardViewModel();

const isDisplayKpiInfo = computed(() => state.value.information.metricType === MetricTypes.Turnover);

const kpiArchevementRate = computed(() =>
  state.value.information.dimensionValueGroups.map((dvg) => {
    const targetSummaryData = dvg.amounts.find((a) => DateService.compareYearMonths(a.yearMonth, state.value.displayYearMonth) === 0);
    const targetKpi = getKpiWithDisplayYearMonth(dvg.kpi);
    if (!targetSummaryData || !targetSummaryData.turnover || !targetKpi) return [0, 0];
    const normal = (targetKpi.kpiValue / targetSummaryData.turnover) * 100;
    if (!targetSummaryData.turnoverWithForecastAccuracy) return [normal, 0];
    return [normal, (targetKpi.kpiValue / targetSummaryData.turnoverWithForecastAccuracy) * 100];
  })
);

const rateColor = computed(() =>
  kpiArchevementRate.value.map((value) => [
    value[0] < 81 ? 'red' : value[0] < 100 ? 'yellow' : 'green',
    value[1] < 81 ? 'red' : value[1] < 100 ? 'yellow' : 'green',
  ])
);

const yMinMax = computed(() => {
  const allValues = state.value.information.dimensionValueGroups.flatMap((dvg) =>
    dvg.amounts.flatMap((a) => [a.turnover ?? 0, a.turnoverWithForecastAccuracy ?? 0])
  );
  return [Math.min(...allValues) * 1.2, Math.max(...allValues) * 1.2];
});

/**
 * 表示年月時点で有効なKPIを取得する。
 * @param kpis - KPI履歴配列
 */
const getKpiWithDisplayYearMonth = (kpis: Kpi[]) => {
  const currentDisplayDate = DateService.convertIYearMonthToLastDateIDate(state.value.displayYearMonth);
  return kpis.find(
    (k) =>
      (!k.valid_from || DateService.compareDates(k.valid_from, currentDisplayDate) !== 1) &&
      (!k.valid_to || DateService.compareDates(k.valid_to, currentDisplayDate) !== -1)
  );
};

const additionalTitles = computed(() => (isDisplayKpiInfo.value && state.value.forecastAccuracyPeriod ? ['', `(${t('調整後')})`] : ['']));
</script>
<template>
  <div class="graph-view">
    <div v-for="(dimensionValueGroup, index) in state.information.dimensionValueGroups" :key="index" class="card__container">
      <div
        v-for="(additionalTitle, titleIndex) in additionalTitles"
        :key="titleIndex"
        class="card"
        :class="`${isDisplayKpiInfo ? 'card--with-kpi' : 'card--normal'} ${titleIndex === 1 ? 'card--adjusted' : ''}`"
      >
        <div class="title">
          <template
            v-if="
              dimensionValueGroup.dimensionValues.name ||
              dimensionValueGroup.dimensionValues.value ||
              dimensionValueGroup.dimensionValues.unit
            "
          >
            {{ dimensionValueGroup.dimensionValues.name }} {{ dimensionValueGroup.dimensionValues.value }}
            {{ dimensionValueGroup.dimensionValues.unit }}
          </template>
          <template v-else>
            {{ t(UNASSIGNED_COLUMN) }}
          </template>
        </div>
        <div v-if="isDisplayKpiInfo" class="rate" :class="`rate--${rateColor[index][titleIndex]}`">
          <div class="rate__info">
            <div>{{ t(vm.metricTypesMapper[state.information.metricType]) }} {{ t(additionalTitle) }}</div>
            <div>{{ t('KPI達成率') }}</div>
          </div>
          <div class="rate__value" :class="`rate__value--${rateColor[index][titleIndex]}`">
            {{ kpiArchevementRate[index] ? kpiArchevementRate[index][titleIndex].toFixed() : '--' }}%
          </div>
        </div>
        <div class="graph">
          <template v-if="isDisplayKpiInfo">
            <KpiChart
              :summary-amounts="dimensionValueGroup.amounts"
              :kpi-data="dimensionValueGroup.kpi"
              :from-year-month="state.displayYearMonthPeriod[0]"
              :to-year-month="state.displayYearMonthPeriod[1]"
              :use-adjusted="titleIndex === 1"
              :min-scale="yMinMax[0]"
              :max-scale="yMinMax[1]"
            />
          </template>
          <template v-else>
            <AmountChart
              :label="t(vm.metricTypesMapper[state.information.metricType])"
              :metric-type="state.information.metricType"
              :amounts="dimensionValueGroup.amounts"
              :from-year-month="state.displayYearMonthPeriod[0]"
              :to-year-month="state.displayYearMonthPeriod[1]"
            />
          </template>
        </div>
        <div v-if="isDisplayKpiInfo" class="value" :class="titleIndex === 1 ? 'value--adjusted' : 'value--normal'">
          <div v-if="titleIndex === 1" class="value__item value__item--adjusted">
            <div class="value__item__title">
              {{ t('正答率') }}
            </div>
            <div class="value__item__number">
              {{
                dimensionValueGroup.amounts
                  .find((a) => DateService.compareYearMonths(a.yearMonth, state.displayYearMonth) === 0)
                  ?.averageForecastAccuracy?.toFixed(2) ?? '--'
              }}
            </div>
          </div>
          <div v-if="titleIndex === 1" class="value__item value__item--adjusted">
            <div class="value__item__title">
              {{ t('調整後') }}
            </div>
            <div class="value__item__number">
              {{
                dimensionValueGroup.amounts
                  .find((a) => DateService.compareYearMonths(a.yearMonth, state.displayYearMonth) === 0)
                  ?.turnoverWithForecastAccuracy?.toFixed(2) ?? '--'
              }}
            </div>
          </div>
          <div class="value__item value__item--result">
            <div class="value__item__title">
              {{ t('実績') }}
            </div>
            <div class="value__item__number">
              {{
                dimensionValueGroup.amounts
                  .find((a) => DateService.compareYearMonths(a.yearMonth, state.displayYearMonth) === 0)
                  ?.turnover?.toFixed(2) ?? '--'
              }}
            </div>
          </div>
          <div class="value__item value__item--target">
            <div class="value__item__title">
              {{ t('目標') }}
            </div>
            <div class="value__item__number">
              {{ getKpiWithDisplayYearMonth(dimensionValueGroup.kpi)?.kpiValue.toFixed(2) ?? '--' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.graph-view {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.card {
  &__container {
    display: grid;
    grid-template-columns: auto;
    grid-template-rows: auto auto;
    gap: 8px;
  }
  display: grid;
  padding: 16px;
  border-radius: 8px;
  gap: 8px;
  border: 1px solid $borderDefault;
  box-shadow: 0px 0px 22px 0px #80808066;
  &--normal {
    height: 380px;
    grid-template-rows: max-content 1fr;
  }
  &--with-kpi {
    height: 400px;
    grid-template-rows: max-content max-content 1fr max-content;
  }
  &--adjusted {
    background-color: #ba68c820;
  }
}

.title {
  color: $textSecondary;
  font-weight: 700;
  font-size: 22px;
  line-height: 1.3;
}
.rate {
  padding: 4px 8px;
  border-radius: 4px;
  &--red {
    background-color: $semanticErrorSubdued;
  }
  &--yellow {
    background-color: $semanticWarningSubdued;
  }
  &--green {
    background-color: $semanticSuccessSubdued;
  }

  &__info {
    display: grid;
    grid-template-columns: max-content max-content;
    justify-content: space-between;
    font-weight: 400;
    font-size: 14px;
    color: $textSecondary;
  }
  &__value {
    font-weight: 700;
    font-style: Bold;
    font-size: 22px;
    text-align: right;
    line-height: 1.3;
    &--red {
      color: $semanticError;
    }
    &--yellow {
      color: $semanticWarning;
    }
    &--green {
      color: $semanticSuccess;
    }
  }
}

.graph {
  min-width: 0;
  overflow: hidden;
}

.value {
  display: grid;
  gap: 11px;
  &--normal {
    grid-template-columns: repeat(2, auto);
  }
  &--adjusted {
    grid-template-columns: repeat(4, auto);
  }
  &__item {
    padding: 4px 8px;
    border-radius: 4px;
    &--result {
      background-color: $semanticPrimarySubdued;
      border: 1px solid $semanticPrimary;
      color: $semanticPrimary;
    }
    &--target {
      background-color: $semanticSuccessSubdued;
      border: 1px solid $semanticSuccess;
      color: $semanticSuccess;
    }
    &--adjusted {
      background-color: #ba68c830;
      border: 1px solid #ba68c8;
      color: #ba68c8;
    }
    &__title {
      font-weight: 400;
      font-size: 12px;
      line-height: 1;
    }
    &__number {
      font-weight: 700;
      font-size: 16px;
      line-height: 1;
      text-align: right;
    }
  }
}
</style>
